// ============================================================================
// Temporary cross-company setup adds — localStorage-backed record/undo store.
//
// Context: when a Master admin re-orders an order placed by a DIFFERENT company
// "to my company" and the order references a machine/wear part not set up for
// the current company, the reorder failure panel offers to add the missing
// items. The user may add them PERMANENTLY (for future orders & quotes) or
// "just this time". A "just this time" add still goes through the SAME backend
// `setup-link-add` endpoint (it really creates the dbo.Wear / company-machine
// row) — there is no backend notion of "temporary". So we record, client-side,
// the EXACT `removeSetupLink` payload needed to undo each temporary add, then
// replay those removals automatically once the order is placed or the quote is
// finalized (and also expose them so the user can remove them in Account Setup).
//
// SAFETY: entries here drive a DESTRUCTIVE `setup-link-remove`. We therefore
// store the precise undo payload (never a derived/broad delete) and scope every
// read/clear strictly by companyId. The store is treated as best-effort cache:
// all access is wrapped so missing/malformed localStorage degrades to an empty
// list and never throws.
// ============================================================================

const STORAGE_KEY = "jcs_temp_setup_adds";

// Composite identity for a single setup link. Mirrors the backend key space for
// a dbo.Wear row (CompanyId, MachineId, AssemblyId, PartId) — for a machine add
// machineId/assemblyId are 0. Used to de-dup so re-adding the same item doesn't
// stack duplicate undo entries.
function compositeKey(entry) {
  return (
    String(entry.companyId || 0) +
    "|" +
    String(entry.type || "") +
    "|" +
    String(entry.refId || 0) +
    "|" +
    String(entry.machineId || 0) +
    "|" +
    String(entry.assemblyId || 0)
  );
}

// Read the whole store as an array. Never throws: malformed/missing → [].
function readAll() {
  try {
    var raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    var parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

// Persist the whole store. Never throws (incognito/quota safe).
function writeAll(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list || []));
  } catch (e) {
    // localStorage unavailable (private mode / quota) — non-fatal. The
    // temporary add simply won't be auto-removed later; the user can still
    // remove it manually in Account Setup.
  }
}

// Append temp-add entries for a company. Each `entry` should be the EXACT
// removeSetupLink payload needed to undo the add, plus display/meta fields:
//   { companyId, type, refId, machineId?, assemblyId?, label, category }
// companyId is coerced to Number and addedAt is stamped here. De-dup is by the
// composite key, so adding the same item twice keeps a single undo entry.
export function addTempSetupAdds(companyId, entries) {
  var cid = Number(companyId);
  if (!cid || !entries || !entries.length) return;
  var list = readAll();
  var seen = {};
  list.forEach(function (e) {
    seen[compositeKey(e)] = true;
  });
  entries.forEach(function (raw) {
    if (!raw) return;
    var entry = {
      companyId: cid,
      type: raw.type,
      refId: raw.refId,
      machineId: raw.machineId || 0,
      assemblyId: raw.assemblyId || 0,
      label: raw.label || "",
      category: raw.category || "",
      addedAt: new Date().toISOString(),
    };
    var key = compositeKey(entry);
    if (seen[key]) return;
    seen[key] = true;
    list.push(entry);
  });
  writeAll(list);
}

// All temp-add entries recorded for the given company.
export function getTempSetupAdds(companyId) {
  var cid = Number(companyId);
  if (!cid) return [];
  return readAll().filter(function (e) {
    return Number(e && e.companyId) === cid;
  });
}

// Remove ONLY the given company's entries; leaves other companies' entries
// untouched.
export function clearTempSetupAdds(companyId) {
  var cid = Number(companyId);
  if (!cid) return;
  var list = readAll();
  var remaining = list.filter(function (e) {
    return Number(e && e.companyId) !== cid;
  });
  writeAll(remaining);
}

// Compact "1 temporary machine and 2 wear parts" phrasing for the toast.
function describeEntries(entries) {
  var machines = entries.filter(function (e) {
    return e.type === "machine";
  }).length;
  var wears = entries.filter(function (e) {
    return e.type === "wear";
  }).length;
  var parts = [];
  if (machines)
    parts.push(
      machines + (machines > 1 ? " temporary machines" : " temporary machine")
    );
  if (wears)
    parts.push(wears + (wears > 1 ? " wear parts" : " wear part"));
  return parts.join(" and ") || "the temporary items";
}

// Auto-remove a company's temporary setup adds. Replays the EXACT recorded
// removeSetupLink payloads (companyId/type/refId + machineId/assemblyId for
// wear). Fires at the order-placed / quote-finalized success moment.
//
// SAFETY CONTRACT:
//  - Only ever calls removeSetupLink with the EXACT recorded entry — never a
//    broad/derived delete.
//  - Strictly scoped to the passed companyId.
//  - Each removal is wrapped in its own try/catch (best-effort): one failure
//    must not throw or block the order/quote success it is attached to.
//  - Empty/malformed store → silent no-op.
//
// Params:
//  - apiService: an FeApiService instance (has removeSetupLink).
//  - companyId : the company whose temp adds to clean up.
//  - toast     : optional fn(message, { title, variant }) for user feedback.
//
// Returns a Promise that ALWAYS resolves (never rejects) with
// { ok, fail, total }.
export function cleanupTempSetupAdds(apiService, companyId, toast) {
  var cid = Number(companyId);
  var entries = cid ? getTempSetupAdds(cid) : [];
  if (!cid || !entries.length) {
    return Promise.resolve({ ok: 0, fail: 0, total: 0 });
  }

  var ok = 0;
  var fail = 0;

  // Chain the removals sequentially; each isolated so a single failure can't
  // reject the chain or abort the rest.
  var chain = Promise.resolve();
  entries.forEach(function (e) {
    chain = chain.then(function () {
      var payload = {
        companyId: cid,
        type: e.type,
        refId: e.refId,
      };
      if (e.type === "wear") {
        payload.machineId = e.machineId || 0;
        payload.assemblyId = e.assemblyId || 0;
      }
      return Promise.resolve()
        .then(function () {
          return apiService.removeSetupLink(payload);
        })
        .then(function () {
          ok += 1;
        })
        .catch(function () {
          // Best-effort: swallow. The entry stays effectively orphaned only if
          // ALL retries fail; we still clear the store below so we don't loop
          // forever, and tell the user they can finish in Account Setup.
          fail += 1;
        });
    });
  });

  return chain
    .then(function () {
      // Clear regardless: the auto-remove is a one-shot at this success moment.
      // Anything that failed is surfaced to the user to handle in Account Setup
      // (re-driving the same removals on every future success is undesirable).
      clearTempSetupAdds(cid);

      if (typeof toast === "function" && (ok > 0 || fail > 0)) {
        var msg =
          "Removed " +
          describeEntries(entries) +
          " that " +
          (entries.length > 1 ? "were" : "was") +
          " added just for this order/quote.";
        if (fail > 0) {
          msg +=
            " " +
            fail +
            (fail > 1 ? " items" : " item") +
            " couldn't be removed automatically — you can remove " +
            (fail > 1 ? "them" : "it") +
            " in Account Setup.";
        }
        toast(msg, {
          title: "Temporary setup cleaned up",
          variant: fail > 0 ? "warning" : "info",
        });
      }
      return { ok: ok, fail: fail, total: entries.length };
    })
    .catch(function () {
      // The chain itself is built to never reject, but guard anyway so callers
      // attached to order/quote success can .then/.catch safely.
      return { ok: ok, fail: fail, total: entries.length };
    });
}
